import React, { useState } from 'react'
import { useLocation, RouteComponentProps } from 'wouter'

import { graphql } from '../../../services/graphql'
import { useStoreState } from '../../../hooks/state'

import { useForm } from '@mantine/form'
import { buildComponent } from '../../../components/factory'

import * as UI from '@mantine/core'
import * as Modals from '@mantine/modals'
import * as Icons from '../../../components/icons'

const CreateGong = buildComponent()
  .withLifecycle(() => {
    const [, setLocation] = useLocation()
    const [submitting, setSubmitting] = useState(false)
    const form = useForm({
      initialValues: {
        code: '',
      },
      validate: {
        code: (value) => {
          if (/^[a-z0-9][a-z0-9_-]+[a-z0-9]$/.test(value)) {
            return null
          }
          return 'Code incompatible'
        },
      },
    })

    const submit = form.onSubmit(async () => {
      try {
        setSubmitting(true)
        form.clearErrors()
        const result = await graphql.mutations.createGong(form.values.code)
        Modals.closeAllModals()
        setLocation('/gongs/' + result.gongCreate.slug)
      } catch (err: any) {
        if ('message' in err) {
          if (err.message.includes('duplicate key')) {
            return form.setFieldError(
              'code',
              'Ce code est déjà utilisé par un autre gong',
            )
          }
        }
        form.setFieldError(
          'code',
          'Une erreur est survenue lors de la création du gong',
        )
      } finally {
        setSubmitting(false)
      }
    })

    return { form, submit, submitting }
  })
  .withRender(({ lifecycle }) => (
    <form onSubmit={lifecycle.submit}>
      <UI.Stack>
        <UI.Text>
          Choisissez un code unique pour votre gong. Le code doit être en
          minuscules et sans espaces.
        </UI.Text>
        <UI.TextInput
          label="Code unique"
          {...lifecycle.form.getInputProps('code')}
        />
        <UI.Button
          mx="auto"
          mb={16}
          sx={{ minHeight: '4rem' }}
          type="submit"
          loading={lifecycle.submitting}
        >
          Créer le gong
        </UI.Button>
      </UI.Stack>
    </form>
  ))

export const ScreenConnectedGongsList = buildComponent<RouteComponentProps>()
  .withLifecycle(() => {
    const [search, setSearch] = useState('')
    const [, setLocation] = useLocation()

    const compare = (...terms: (string | undefined)[]) => {
      return terms.some((t) => t?.toLowerCase().includes(search.toLowerCase()))
    }

    const gongs = useStoreState((store) => store.audio.gongs).filter((g) =>
      compare(g.short?.fr, g.short?.en, g.slug),
    )

    const createGong = () => {
      Modals.openModal({
        title: <UI.Text weight="bold">Créer un gong</UI.Text>,
        children: <CreateGong />,
        padding: 'lg',
      })
    }

    return {
      search,
      setSearch,
      gongs,
      createGong,
      setLocation,
    }
  })
  .withRender(({ lifecycle }) => (
    <React.Fragment>
      <UI.Stack
        mx={-16}
        px={16}
        mt={-16}
        py={16}
        sx={{
          position: 'sticky',
          top: 72,
          zIndex: 10,
          backgroundColor: '#f8f9fc',
        }}
        spacing={0}
      >
        <UI.Group position="apart">
          <UI.Stack spacing={0}>
            <UI.Title>
              <UI.Text size={22}>Gongs</UI.Text>
            </UI.Title>
            <UI.Text color="dimmed">
              Configuration des gongs utilisés pour marquer les transitions
              durant les siestes.
            </UI.Text>
          </UI.Stack>
          <UI.Button onClick={lifecycle.createGong}>Créer un gong</UI.Button>
        </UI.Group>

        <UI.TextInput
          mt={16}
          icon={<Icons.FontAwesomeIcon icon={Icons.Solid.faSearch} />}
          placeholder="Rechercher un gong..."
          value={lifecycle.search}
          onChange={(e) => lifecycle.setSearch(e.currentTarget.value)}
        />
      </UI.Stack>

      <UI.SimpleGrid cols={3} spacing="lg" mt="lg">
        {lifecycle.gongs.map((gong) => (
          <UI.Card
            key={gong._id}
            shadow="sm"
            p="lg"
            radius="md"
            withBorder
            sx={{ cursor: 'pointer' }}
            onClick={() => lifecycle.setLocation('/gongs/' + gong.slug)}
          >
            <UI.Card.Section>
              <UI.Image
                src={
                  gong.image
                    ? `${process.env.REACT_APP_STATIC_URL}images/gongs/${gong.slug}/${gong.image}.webp`
                    : undefined
                }
                height={160}
                alt={gong.short?.fr}
                withPlaceholder
              />
            </UI.Card.Section>

            <UI.Group position="apart" mt="md" mb="xs">
              <UI.Text weight={500}>{gong.short?.fr}</UI.Text>
            </UI.Group>

            <UI.Text size="sm" color="dimmed">
              {gong.slug}
            </UI.Text>
          </UI.Card>
        ))}
      </UI.SimpleGrid>

      {lifecycle.gongs.length === 0 && lifecycle.search === '' && (
        <UI.Center mt="xl">
          <UI.Stack align="center" spacing="md">
            <UI.Text color="dimmed">
              Aucun gong configuré pour le moment.
            </UI.Text>
            <UI.Button onClick={lifecycle.createGong}>
              Créer le premier gong
            </UI.Button>
          </UI.Stack>
        </UI.Center>
      )}

      {lifecycle.gongs.length === 0 && lifecycle.search !== '' && (
        <UI.Center mt="xl">
          <UI.Text color="dimmed">
            Aucun gong ne correspond à votre recherche.
          </UI.Text>
        </UI.Center>
      )}
    </React.Fragment>
  ))
